"use client";
// import type { MyInlineBlock, MyNumberBlock, MyTextBlock } from "@/payload-types";
import type { DefaultNodeTypes, SerializedInlineBlockNode } from "@payloadcms/richtext-lexical";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { type JSXConvertersFunction, RichText } from "@payloadcms/richtext-lexical/react";
import React, { createContext, useContext, useEffect, useState } from "react";
// not from TextWithImageBlock: that is the admin editor component, and importing it pulled Payload's admin UI into
// the public site and created an import cycle with this file
import { ImageSize } from "@/src/collections/enums";
import { getBlobUrl } from "@/src/components/utils/utils";
import RelationLink from "@/src/components/RelationLink";

// Extend the default node types with your custom blocks for full type safety
type NodeTypes = DefaultNodeTypes;

// type NodeTypes =
//     | DefaultNodeTypes
//     | SerializedBlockNode<MyNumberBlock | MyTextBlock>
//     | SerializedInlineBlockNode<MyInlineBlock>;

type TextNode = { type: string; text?: string; children?: TextNode[] };

const nodesToText = (nodes: TextNode[] = []): string =>
    nodes
        .map((n) => {
            if (n.type === "linebreak") return " ";
            if (n.type === "text") return n.text ?? "";
            return n.children ? nodesToText(n.children) : "";
        })
        .join("");

const slugify = (text: string) =>
    text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");

const getInlineRelationLink = (node: SerializedInlineBlockNode) => {
    const fields = node.fields;
    const { doc } = fields;
    return <RelationLink relationTo={doc.relationTo} id={doc.value.slug || doc.value} label={fields.label} />;
};

export type SourceItemType = {
    id: string;
    index: number;
    quote: string | null;
    externalLink: string | null;
    internalLinkId: string | null;
    internalLinkSlug: string | null;
    internalLinkType: string | null;
    internalLinkLabel: string | null;
};

type LexicalNode = { type?: string; tag?: string; children?: LexicalNode[]; fields?: Record<string, unknown> };
type RichTextValue = { root: LexicalNode } | null | undefined;

/**
 * Visits every node of a rich text value in document order. Also descends into rich text nested in block fields
 * (e.g. TextWithImage's text), which is rendered in place, so the order matches the page.
 */
const walkRichText = (state: RichTextValue, visit: (node: LexicalNode) => void) => {
    const walk = (node: LexicalNode) => {
        visit(node);
        for (const value of Object.values(node.fields ?? {})) {
            const nested = (value as { root?: LexicalNode } | null)?.root;
            if (nested) walk(nested);
        }
        node.children?.forEach(walk);
    };
    if (state?.root) walk(state.root);
};

export type HeadingItem = { tag: "h1" | "h2" | "h3"; text: string; id: string };

/** The h1–h3 headings of a rich text value, with the same ids the heading converter gives them. */
export const collectHeadings = (state: RichTextValue): HeadingItem[] => {
    const headings: HeadingItem[] = [];
    walkRichText(state, (node) => {
        if (node.type === "heading" && (node.tag === "h1" || node.tag === "h2" || node.tag === "h3")) {
            const text = nodesToText(node.children as TextNode[]);
            headings.push({ tag: node.tag, text, id: slugify(text) });
        }
    });
    return headings;
};

/** The inlineSource blocks of a rich text value in document order, numbered from 1. */
export const collectSources = (state: RichTextValue): SourceItemType[] => {
    const sources: SourceItemType[] = [];
    walkRichText(state, (node) => {
        const fields = node.fields;
        if (fields?.blockType === "inlineSource") {
            const f = fields as SerializedInlineBlockNode["fields"];
            // the populated character/group, or only its id when the content was fetched at depth 0
            const linked = f.internalLink?.value;
            sources.push({
                id: f.id,
                index: sources.length + 1,
                quote: f.quote ?? null,
                externalLink: f.linkType === "external" ? (f.externalLink ?? null) : null,
                internalLinkId: (typeof linked === "object" ? linked?.id : linked)?.toString() ?? null,
                internalLinkSlug: typeof linked === "object" ? (linked?.slug ?? null) : null,
                internalLinkType: f.internalLink?.relationTo ?? null,
                internalLinkLabel: f.internalLink?.label ?? null,
            });
        }
    });
    return sources;
};

/** Source id → its number in the sources list; inlineSource links render as "[n]" when their id is in here. */
export const SourceIndexContext = createContext<ReadonlyMap<string, number>>(new Map());

const InlineSourceLink = ({ node }: { node: SerializedInlineBlockNode }) => {
    const index = useContext(SourceIndexContext).get(node.fields.id);
    return (
        <sup>
            <a
                id={`link-${node.fields.id}`}
                data-quote={node.fields.quote}
                data-externallink={node.fields.linkType === "external" ? node.fields.externalLink : ""}
                data-internallinkid={node.fields.internalLink?.value?.id}
                data-internallinklabel={node.fields.internalLink?.label}
                data-internallinktype={node.fields.internalLink?.relationTo}
                className="sourceLink"
                href={`#source-${node.fields.id}`}
            >
                {index ? `[${index}]` : node.fields.label || node.fields.internalLink?.label}
            </a>
        </sup>
    );
};

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
    ...defaultConverters,
    paragraph: ({ node, nodesToJSX }) => {
        const children = nodesToJSX({ nodes: node.children });
        // TextWithImage renders block content (its caption and nested rich text with its own <p>s), which may not sit
        // inside a <p>: the browser would split the <p> and hydration would fail. Such a paragraph becomes a <div>,
        // with prose's paragraph margins so the spacing stays the same.
        const hasBlockContent = node.children.some(
            (c) => c.type === "inlineBlock" && (c as SerializedInlineBlockNode).fields.blockType === "TextWithImage",
        );
        const content = children?.length ? children : <br />;
        return hasBlockContent ? <div className="my-[1.25em]">{content}</div> : <p>{content}</p>;
    },
    heading: ({ node, nodesToJSX }) => {
        const content = nodesToJSX({ nodes: node.children }); // keep as JSX
        const id = slugify(nodesToText(node.children)); // plain string for id/href

        const link = (
            <span className="header-link">
                <a className="no-underline" href={`#${id}`}>
                    {content}
                </a>
            </span>
        );

        switch (node.tag) {
            case "h1":
                return <h1 id={id}>{link}</h1>;
            case "h2":
                return <h2 id={id}>{link}</h2>;
            case "h3":
                return <h3 id={id}>{link}</h3>;
            default:
                // @ts-expect-error -- defaultConverters.heading is typed as a converter or a static ReactNode
                return defaultConverters.heading?.({ node, nodesToJSX }) ?? null;
        }
    },
    inlineBlocks: {
        ...defaultConverters.inlineBlocks,
        // Each key should match your inline block's slug
        inlineSource: ({ node }: { node: SerializedInlineBlockNode }) => <InlineSourceLink node={node} />,
        inlineRelation: ({ node }: { node: SerializedInlineBlockNode }) => <span>{getInlineRelationLink(node)}</span>,
        inlineImage: ({ node }: { node: SerializedInlineBlockNode }) => (
            <span className="float-right">
                {/* straight from blob storage: Media.url is Payload's /api/media/file route, which proxies the file */}
                <img
                    src={getBlobUrl(node.fields.image.sizes?.avif?.url || node.fields.image.url)}
                    width={300}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    alt={node.fields.image.alt ?? ""}
                    style={{ width: "400px" }}
                />
            </span>
        ),
        TextWithImage: ({ node }: { node: SerializedInlineBlockNode }) => {
            const isMobile = useIsMobile();

            return (
                // block elements: the paragraph converter renders the surrounding paragraph as a <div>
                <div>
                    <div
                        className={`${node.fields.imageSide === "right" ? "float-right ml-4 mb-4" : "float-left mb-4 mr-12"} ${!isMobile ? (node.fields.imageSize === ImageSize.SMALL ? "w-1/4" : node.fields.imageSize === ImageSize.MEDIUM ? "w-1/3" : "w-1/2") : ""} border-white border not-prose bg-gray-900 rounded-sm`}
                    >
                        <img
                            className="pb-1 rounded-sm w-full"
                            src={getBlobUrl(node.fields.image?.sizes?.avif?.url || node.fields.image?.url)}
                            alt={node.fields.image?.alt ?? ""}
                        />
                        <div className="pl-2 pr-2 pb-1 pt-1">{node.fields.imageText}</div>
                    </div>
                    <div>
                        <RichText converters={jsxConverters} data={node.fields.text} />
                    </div>
                </div>
            );
        },
    },
});

export const MyComponent: React.FC<{
    lexicalData: SerializedEditorState;
}> = ({ lexicalData }) => {
    return <RichText converters={jsxConverters} data={lexicalData} />;
};

export function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < breakpoint);
        };

        handleResize(); // initial check
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [breakpoint]);

    return isMobile;
}

export default jsxConverters;
